// The wake capability envelope — the client half of the contract the gateway's
// `decryptCapability` is the server half of (server/gateway/main.ts).
//
// The construction the spec names HPKE-P256-HKDF-SHA256-AES128GCM, composed
// from WebCrypto primitives: per recipient key, a fresh ephemeral P-256 pair →
// ECDH with the gateway key → HKDF-SHA256 (empty salt, the purpose string as
// info, 16 bytes) → AES-128-GCM with a random 12-byte nonce. Wire form:
// base64url of `{"version":2,"recipients":[{key_id, ephemeral_key, nonce,
// ciphertext}]}`. The envelope is encrypted to BOTH gateway keys — current and
// next — so a key rotation is a planned operation, not a mass invalidation.

import { b64uDecode, b64uEncode } from '$lib/crypto/base64url';
import { fingerprintOfKey, utf8 } from '$lib/crypto/hash';

const CAPABILITY_PURPOSE = 'imp/1 capability';
const ENVELOPE_VERSION = 2;

export interface WebPushCapability {
  platform: 'webpush';
  /** The subscription endpoint URL — the push service's name for this device. */
  device_token: string;
  /** The subscription's encryption keys, required for webpush: the gateway
   *  seals the forwarded preview to them (RFC 8291). */
  web_push_keys: { p256dh: string; auth: string };
  valid_until: string;
  contact_id: string;
  gateway_id: string;
  /** Fingerprint of the distributor's server key — named consent: only the
   *  distributor the buyer handed this to may use it. */
  distributor: string;
}

interface Recipient {
  key_id: string;
  ephemeral_key: string;
  nonce: string;
  ciphertext: string;
}

async function sealTo(gatewayKeyB64: string, plaintext: Uint8Array): Promise<Recipient> {
  const ephemeral = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveBits'],
  );
  const ephemeralPublic = new Uint8Array(await crypto.subtle.exportKey('raw', ephemeral.publicKey));
  const gatewayKey = await crypto.subtle.importKey(
    'raw',
    b64uDecode(gatewayKeyB64) as BufferSource,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );
  const shared = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: gatewayKey },
    ephemeral.privateKey,
    256,
  );
  const hkdf = await crypto.subtle.importKey('raw', shared, 'HKDF', false, ['deriveBits']);
  const contentKey = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(0) as BufferSource,
      info: utf8(CAPABILITY_PURPOSE) as BufferSource,
    },
    hkdf,
    128,
  );
  const aes = await crypto.subtle.importKey('raw', contentKey, 'AES-GCM', false, ['encrypt']);
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: nonce as BufferSource, tagLength: 128 },
      aes,
      plaintext as BufferSource,
    ),
  );
  return {
    key_id: await fingerprintOfKey(gatewayKeyB64),
    ephemeral_key: b64uEncode(ephemeralPublic),
    nonce: b64uEncode(nonce),
    ciphertext: b64uEncode(ciphertext),
  };
}

/** Encrypts the envelope to every given gateway key and encodes the wire form.
 *  The distributor carries the result as an opaque string; only the gateway can
 *  open it. */
export async function createCapability(
  envelope: WebPushCapability,
  gatewayKeysB64: string[],
): Promise<string> {
  const plaintext = utf8(JSON.stringify(envelope));
  const recipients: Recipient[] = [];
  for (const keyB64 of gatewayKeysB64) {
    recipients.push(await sealTo(keyB64, plaintext));
  }
  if (recipients.length === 0) {
    throw new Error('capability needs at least one gateway key');
  }
  return b64uEncode(utf8(JSON.stringify({ version: ENVELOPE_VERSION, recipients })));
}
